import type { TripConfig } from '../types';
import { canEnterBranch, type ItineraryBranch } from './itineraryBranches';
import { buildSettleData, computeMyBalance, expenseBaseCurrency, isLiveExpense, type SettleGroupData } from './expenseUtils';

/** Keep writable scopes separate, including when expense IDs coincide. */
export function accessibleExpenseScopes(root: TripConfig, branches: ItineraryBranch[], configs: Record<string, TripConfig>, userId: string): TripConfig[] {
  if (!root.memberIds.includes(userId)) return [];
  return [root, ...branches.filter(branch => canEnterBranch(branches, branch.id, userId))
    .flatMap(branch => {
      const config = configs[branch.id];
      return config?.memberIds.includes(userId) && config.id === `${root.id}/branches/${branch.id}` ? [config] : [];
    })]
    .filter((scope, index, scopes) => scopes.findIndex(other => other.id === scope.id) === index);
}

export function combinedSettlementGroups(scopes: TripConfig[], userId: string) {
  const result = { oweGroups: [] as SettleGroupData[], owedGroups: [] as SettleGroupData[], settledGroups: [] as SettleGroupData[] };
  for (const scope of scopes) {
    const expenses = [...new Map((scope.expenses ?? []).map(e => [e.id, e])).values()];
    const groups = buildSettleData(expenses, userId, scope.travellers ?? [], scope.destinations ?? []);
    for (const key of ['oweGroups', 'owedGroups', 'settledGroups'] as const) for (const group of groups[key]) {
      const existing = result[key].find(entry => entry.other.id === group.other.id);
      if (!existing) result[key].push(group);
      else {
        existing.rows.push(...group.rows);
        existing.total += group.total;
        if (!group.formerTraveller) { existing.other = group.other; existing.formerTraveller = false; }
      }
    }
  }
  for (const groups of Object.values(result)) groups.sort((a, b) => b.total - a.total);
  return result;
}

export type BranchExpenseSummary = {
  status: 'ready' | 'loading' | 'unavailable' | 'currency-review';
  currency: string; total: number | null; balance: number | null; count: number;
};
export const expenseSummaryMessage = {
  loading: 'Updating expense totals…',
  unavailable: 'Reconnect to load your expense totals.',
  'currency-review': 'Some expenses need a currency review before totals can be combined.',
  ready: '',
};

/** Read-only figures, never a flattened TripConfig that could be saved to root. */
export function branchExpenseSummary(root: TripConfig, branches: ItineraryBranch[], configs: Record<string, TripConfig>,
  userId: string, loading = false, error = ''): BranchExpenseSummary {
  const currency = expenseBaseCurrency(root);
  const incomplete = (status: BranchExpenseSummary['status']): BranchExpenseSummary => ({ status, currency, total: null, balance: null, count: 0 });
  if (error || !root.memberIds.includes(userId)) return incomplete('unavailable');
  if (loading || !root.expenses) return incomplete('loading');
  const scopes = new Map([[root.id, root]]);
  for (const branch of branches) {
    if (!branch.memberIds.includes(userId)) continue;
    if (!canEnterBranch(branches, branch.id, userId)) return incomplete('unavailable');
    const config = configs[branch.id];
    if (!config?.expenses) return incomplete('loading');
    if (!config.memberIds.includes(userId) || config.id !== `${root.id}/branches/${branch.id}`) return incomplete('unavailable');
    scopes.set(config.id, config);
  }
  let total = 0, balance = 0, count = 0;
  for (const scope of scopes.values()) {
    const expenses = [...new Map((scope.expenses ?? []).map(e => [e.id, e])).values()];
    // Never turn a missing conversion into zero or sum differently labelled units.
    if (expenses.some(e => !isLiveExpense(e) || e.targetCurrency !== currency)) return incomplete('currency-review');
    total += expenses.reduce((sum, e) => sum + e.targetAmount, 0);
    balance += computeMyBalance(expenses, userId, scope.travellers ?? [], scope.destinations ?? []);
    count += expenses.length;
  }
  return { status: 'ready', currency, total, balance: Math.round(balance * 100) / 100, count };
}
